'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { CircularProgress, PulseButton, Search } from 'src/components';
import { StrategyTable, SortField, SortDirection } from '../StrategyTable';
import { StrategyDeleteModal } from '../StrategyDeleteModal';
import {
  useGetAllTargetStrategiesQuery,
  useDeleteTargetStrategyMutation,
} from 'src/api/redux';
import { useChainMap } from '../../hooks';
import { useNotification } from 'src/context/notification';
import { useLogging } from 'src/context/logging';
import useStrategies from 'src/hooks/strategy/useStrategies';
import { LOCATION } from 'src/hooks/filters/constants';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { useDebounce } from 'use-debounce';
import { StrategyBuilderButton } from '../StrategyBuilderButton';
import { useFlags } from 'launchdarkly-react-client-sdk';

const TargetStrategiesGrid = () => {
  const router = useRouter();
  const log = useLogging();
  const { setNotification } = useNotification();
  const { useStrategyBuilder } = useFlags();
  // Search state
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 300);

  // Sort state
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Build query params for server-side sorting and searching
  const queryParams = {
    ...(debouncedSearch && { q: debouncedSearch }),
    sort: sortField,
    order: sortDirection,
  };

  const { data: strategies, isLoading } = useGetAllTargetStrategiesQuery(
    queryParams,
    {
      pollingInterval: 60_000,
    }
  );
  const [deleteStrategy, { isLoading: isDeleting }] =
    useDeleteTargetStrategyMutation();

  const chainMap = useChainMap('target');
  const { selectedStrategy, setSelectedStrategy } = useStrategies(
    LOCATION.STRATEGY_MANAGER
  );

  const [strategyToDelete, setStrategyToDelete] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleSort = (field: SortField, direction: SortDirection) => {
    setSortField(field);
    setSortDirection(direction);
  };

  const handleAddStrategy = () => {
    log.event('Add strategy button clicked', {
      source: 'TargetStrategiesGrid',
      strategyMode: 'target',
    });
    router.push('/settings/admin/strategy-manager/target/add');
  };

  const handleEditStrategy = (id: string) => {
    const strategy = strategies?.results?.find((s) => s.id.toString() === id);

    log.event('Edit strategy clicked', {
      source: 'TargetStrategiesGrid',
      strategyId: id,
      strategyLabel: strategy?.label,
      strategyType: strategy?.type,
    });

    router.push(`/settings/admin/strategy-manager/target/${id}/edit`);
  };

  const handleCopyStrategy = (id: string) => {
    const strategy = strategies?.results?.find((s) => s.id.toString() === id);

    log.event('Copy strategy clicked', {
      source: 'TargetStrategiesGrid',
      strategyId: id,
      strategyLabel: strategy?.label,
      strategyType: strategy?.type,
    });

    router.push(`/settings/admin/strategy-manager/target/copy/${id}`);
  };

  const handleShowDialog = (strategy) => {
    setStrategyToDelete(strategy);
    setShowDeleteDialog(true);
  };

  const handleClose = () => {
    setStrategyToDelete(null);
    setShowDeleteDialog(false);
  };

  const handleDeleteStrategy = async (id: string) => {
    const strategy = strategies?.results?.find((s) => s.id.toString() === id);
    try {
      await deleteStrategy(id);
      log.event('Strategy deleted', {
        source: 'TargetStrategiesGrid',
        strategyId: id,
        strategyLabel: strategy?.label,
        strategyType: strategy?.type,
      });
      setNotification({
        title: 'Strategy deleted',
        message: 'Strategy deleted successfully',
        type: 'success',
      });
    } catch (err) {
      log.exception(`Error deleting strategy: ${err.data?.error_message}`);
      setNotification({
        title: 'Error deleting strategy',
        message: err?.data?.error_message,
        type: 'error',
      });
    } finally {
      setShowDeleteDialog(false);
      setStrategyToDelete(null);
      if (selectedStrategy?.id?.toString() === id) {
        setSelectedStrategy(null);
      }
    }
  };

  const isAnyLoading = isLoading || isDeleting;

  return (
    <>
      <div className="relative space-y-4">
        {/* Add New Strategy Button */}
        <div className="flex justify-between">
          <Search
            value={search}
            onSearch={setSearch}
            size="sm"
            placeholder="Search strategies..."
            className="rounded-full min-w-64"
          />
          <div className="flex gap-2">
            {useStrategyBuilder && <StrategyBuilderButton />}
            <PulseButton
              variant="solid"
              color="primary"
              size="sm"
              startDecorator={faPlus}
              onClick={handleAddStrategy}
              data-testid="new-strategy-button"
              aria-label="Add new strategy"
            >
              New Strategy
            </PulseButton>
          </div>
        </div>

        {/* Strategy Table */}
        <div
          className={`transition-all duration-200 ${
            isAnyLoading ? 'blur-sm pointer-events-none' : ''
          }`}
        >
          <StrategyTable
            strategies={strategies?.results || []}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
            onRowClick={(strategy) =>
              handleEditStrategy(strategy.id.toString())
            }
            onEdit={(strategy) => handleEditStrategy(strategy.id.toString())}
            onDelete={(strategy) => handleShowDialog(strategy)}
            onCopy={(strategy) => handleCopyStrategy(strategy.id.toString())}
            chainMap={chainMap}
          />
        </div>

        {/* Loading overlay */}
        {isAnyLoading && (
          <div className="absolute inset-0 flex justify-center items-center bg-white/10 backdrop-blur-sm">
            <CircularProgress size="md" color="neutral" />
          </div>
        )}
      </div>

      <StrategyDeleteModal
        open={showDeleteDialog}
        onClose={handleClose}
        onDelete={handleDeleteStrategy}
        strategy={strategyToDelete}
      />
    </>
  );
};

export default TargetStrategiesGrid;
